import { useState } from "react";
import { toast } from "sonner";
import { Copy } from "lucide-react";
import { Field, TextArea, Select, PrimaryButton, GhostButton, ShareButton } from "@/tools/ui";

const OutBox = ({ value, testid }) => (
  <div className="border border-border bg-muted">
    <div className="flex items-center justify-between border-b border-border px-4 py-2">
      <span className="overline text-muted-foreground">Result</span>
      <div className="flex gap-2">
        <GhostButton onClick={() => { navigator.clipboard.writeText(value); toast.success("Copied"); }} className="px-3 py-1.5 text-xs"><Copy className="h-3 w-3" /> Copy</GhostButton>
        <ShareButton testid={`${testid}-share`} text={value} />
      </div>
    </div>
    <pre data-testid={testid} className="overflow-x-auto whitespace-pre-wrap break-words px-4 py-4 text-sm">{value}</pre>
  </div>
);

export const CaseConverter = () => {
  const [text, setText] = useState("");
  const [out, setOut] = useState("");
  const convert = (mode) => {
    if (mode === "upper") setOut(text.toUpperCase());
    else if (mode === "lower") setOut(text.toLowerCase());
    else if (mode === "title") setOut(text.replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()));
    else if (mode === "sentence") setOut(text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (c) => c.toUpperCase()));
  };
  return (
    <div className="space-y-6">
      <Field label="Your text"><TextArea data-testid="case-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} placeholder="Type or paste text…" /></Field>
      <div className="flex flex-wrap gap-3">
        <PrimaryButton data-testid="case-upper" onClick={() => convert("upper")}>UPPERCASE</PrimaryButton>
        <GhostButton data-testid="case-lower" onClick={() => convert("lower")}>lowercase</GhostButton>
        <GhostButton data-testid="case-title" onClick={() => convert("title")}>Title Case</GhostButton>
        <GhostButton data-testid="case-sentence" onClick={() => convert("sentence")}>Sentence case</GhostButton>
      </div>
      {out && <OutBox value={out} testid="case-output" />}
    </div>
  );
};

export const TextReverse = () => {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("chars");
  const [out, setOut] = useState("");
  const run = () => {
    if (mode === "chars") setOut([...text].reverse().join(""));
    else if (mode === "words") setOut(text.split(/\s+/).reverse().join(" "));
    else setOut(text.split(/\n/).reverse().join("\n"));
  };
  return (
    <div className="space-y-6">
      <Field label="Reverse by"><Select data-testid="reverse-mode" value={mode} onChange={(e) => setMode(e.target.value)}><option value="chars">Characters</option><option value="words">Words</option><option value="lines">Lines</option></Select></Field>
      <Field label="Your text"><TextArea data-testid="reverse-input" rows={5} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      <PrimaryButton data-testid="reverse-run" onClick={run}>Reverse</PrimaryButton>
      {out && <OutBox value={out} testid="reverse-output" />}
    </div>
  );
};

export const RemoveDuplicateLines = () => {
  const [text, setText] = useState("");
  const [out, setOut] = useState("");
  const [removed, setRemoved] = useState(0);
  const run = () => {
    const lines = text.split(/\n/);
    const seen = new Set();
    const kept = lines.filter((l) => { const k = l.trim(); if (seen.has(k)) return false; seen.add(k); return true; });
    setRemoved(lines.length - kept.length);
    setOut(kept.join("\n"));
  };
  return (
    <div className="space-y-6">
      <Field label="Your text (one item per line)"><TextArea data-testid="dedupe-input" rows={7} value={text} onChange={(e) => setText(e.target.value)} /></Field>
      <PrimaryButton data-testid="dedupe-run" onClick={run}>Remove Duplicates</PrimaryButton>
      {out && (<><p className="text-sm">Removed <b className="text-primary">{removed}</b> duplicate line(s).</p><OutBox value={out} testid="dedupe-output" /></>)}
    </div>
  );
};

export const Slugify = () => {
  const [text, setText] = useState("");
  const out = text.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/[\s-]+/g, "-").replace(/(^-|-$)/g, "");
  return (
    <div className="space-y-6">
      <Field label="Text to slugify"><TextArea data-testid="slug-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder="My Awesome Blog Post!" /></Field>
      {out && <OutBox value={out} testid="slug-output" />}
    </div>
  );
};
